import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Card, CardContent, CardHeader, Collapse, IconButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Draggable, { DraggableProps } from 'react-draggable';
import { Enable, Resizable, Size } from 're-resizable';

const Flying = styled.div<{ visible: boolean }>(props => ({
    position: 'absolute',
    top: 0,
    left: 0,
    boxSizing: 'content-box',
    pointerEvents: 'none',
    visibility: props.visible ? 'visible' : 'hidden',
}));

const Inner = styled.div({
    pointerEvents: 'auto',
});

export type MovableFrameProps = React.PropsWithChildren<{
    title?: string;
    titleStyle?: CSSProperties;
    visible?: boolean;
    shrunk?: boolean;
    shrinkable?: boolean;
    resizable?: boolean;
    width?: Size['width'];
    height?: Size['height'];
    top?: DraggableProps['position']['y'];
    left?: DraggableProps['position']['x'];
    onShrink?: (shrunk: boolean) => void;
}>;

export function MovableFrame(props: MovableFrameProps) {
    const {
        title,
        titleStyle = {},
        visible = true,
        shrunk,
        shrinkable = shrunk !== undefined,
        resizable = true,
        width, height,
        top: initialTop = 0, left: initialLeft = 0,
        onShrink,
        children,
    } = props;

    const position = useMemo(() => ({ x: initialLeft, y: initialTop }), [initialLeft, initialTop]);

    const [expanded, setExpanded] = useState(() => shrunk !== undefined ? !shrunk : true);
    useEffect(() => {
        if (shrunk === undefined) return;
        setExpanded(!shrunk);
    }, [shrunk]);
    const onExpand = useCallback((isCurrentlyExpanded: boolean) => {
        if (onShrink) {
            onShrink(!isCurrentlyExpanded);
        } else {
            setExpanded(prev => !prev);
        }
    }, [onShrink]);

    const resizingRef = useRef(false);

    const resizableHandles = useMemo<Enable>(() => ({
        top: resizable,
        right: resizable,
        bottom: resizable,
        left: resizable,
        topRight: resizable,
        bottomLeft: resizable,
        topLeft: resizable,
        bottomRight: resizable,
    }), [resizable]);

    return (
        <Flying visible={visible}>
            <Draggable
                defaultPosition={position}
                onStart={(): void | false => {
                    if (resizingRef.current) return false;
                    return;
                }}
            >
                <Inner>
                    <Card raised sx={{
                        width: 'fit-content',
                        padding: 0,
                        '&:last-child': {
                            paddingBottom: 0
                        },
                        minWidth: '250px',
                    }}>
                        <CardHeader
                            sx={{
                                backgroundColor: 'lightgray',
                                padding: '5px 10px',
                                ...titleStyle
                            }}
                            title={title}
                            titleTypographyProps={{ variant: 'h6' }}
                            action={
                                shrinkable
                                    ? <IconButton onClick={() => onExpand(expanded)} size="large">
                                        {expanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                    : <></>
                            }
                        />
                        <Collapse in={expanded}>
                            <CardContent sx={{
                                width: 'fit-content',
                                padding: 0,
                                '&:last-child': {
                                    paddingBottom: 0
                                },
                                minWidth: '250px',
                            }}>
                                <Resizable
                                    defaultSize={width !== undefined && height !== undefined ? { width, height } : undefined}
                                    onResizeStart={() => {
                                        resizingRef.current = true;
                                    }}
                                    onResizeStop={() => {
                                        resizingRef.current = false;
                                    }}
                                    enable={resizableHandles}
                                    // Because overall width of MovableFrame is determined with the
                                    // widest width of its children, when the width of Header is wider
                                    // than Resizable, the resize handle might appear inside the Content
                                    // region instead of the edge of it.
                                    // One possible solution might be tracking the width of the Header
                                    // and update minWidth with it, but I'm not sure it would work...
                                    minWidth={'250px'}
                                >
                                    {children}
                                </Resizable>
                            </CardContent>
                        </Collapse>
                    </Card>
                </Inner>
            </Draggable >
        </Flying>
    );
}
